'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Logger } from '@/lib/logger';
import { CLIENT_VERSION_INFO } from '@/lib/version-client';

/**
 * Realtime liveness watchdog (#702).
 *
 * Supabase Realtime reports `SUBSCRIBED` as soon as the socket joins the
 * channel - it says nothing about whether `postgres_changes` payloads will
 * ever arrive. Every PureBox consumer treated `SUBSCRIBED` as proof that
 * events were flowing and switched its fallback poller off, so a silent
 * channel froze the UI permanently with no error surfaced. In practice the
 * channel is silent for every browser session, because the `authenticated`
 * role has no table-level `GRANT` and Realtime drops each change event before
 * RLS is even reached.
 *
 * This hook inverts the assumption. Realtime is a latency optimisation, never
 * a source of truth:
 *
 *   - `isLive` is false until a real payload has been observed, so the poller
 *     runs by default and the product works with zero Realtime events.
 *   - `SUBSCRIBED` alone never sets `isLive`; it only starts the silence
 *     countdown.
 *   - Once events flow, a fresh silence window is armed on every payload. If
 *     the stream goes quiet the hook demotes back to polling within a bounded
 *     window instead of waiting for a socket error that may never come.
 *   - The first time the watchdog fires for a mounted consumer it emits one
 *     `Logger.warn('realtime_channel_silent', …)`, so a permanently silent
 *     channel is visible in Better Stack instead of invisible.
 */

/**
 * How long a subscribed channel may stay silent before we stop trusting it.
 *
 * Long enough that a normal gap between scan waves does not demote a healthy
 * channel, short enough that recovery is well inside a user's patience.
 * Demotion is cheap and self-correcting: the poller resumes, and the next real
 * payload promotes the channel straight back to live.
 */
export const REALTIME_SILENCE_TIMEOUT_MS = 20_000;

export interface RealtimeLiveness {
  /**
   * True only while real `postgres_changes` payloads are known to be flowing.
   * Gate a fallback poller on `!isLive`.
   */
  isLive: boolean
  /** Call when `.subscribe()` reports `SUBSCRIBED`. Does not imply liveness. */
  markSubscribed: () => void
  /** Call from the `postgres_changes` handler, on every real payload. */
  markEvent: () => void
  /** Call on `CHANNEL_ERROR` / `TIMED_OUT` / `CLOSED` and on teardown. */
  markDisconnected: () => void
}

export interface UseRealtimeLivenessOptions {
  /** Test seam. Defaults to {@link REALTIME_SILENCE_TIMEOUT_MS}. */
  silenceMs?: number
  /**
   * Whether a silent channel is worth reporting. Defaults to `true`.
   *
   * Set `false` for channels where silence is the *expected* steady state, not
   * an anomaly. `scan_jobs` during a running scan should produce a steady
   * stream, so silence there is evidence of a fault. An activity feed on an
   * idle dashboard produces nothing for long stretches by design, so warning
   * about it would fire on every mount even with Realtime working perfectly.
   *
   * This only suppresses the telemetry. The channel is still demoted and the
   * consumer's fallback still takes over, because a quiet channel cannot be
   * trusted either way.
   */
  warnOnSilence?: boolean
}

/**
 * @param channel Stable, low-cardinality label for the subscription (e.g.
 * `'onboarding_sample_scan'`). Used for Better Stack grouping - never pass a
 * raw channel name containing a job or user id.
 */
export function useRealtimeLiveness(
  channel: string,
  {
    silenceMs = REALTIME_SILENCE_TIMEOUT_MS,
    warnOnSilence = true,
  }: UseRealtimeLivenessOptions = {},
): RealtimeLiveness {
  const [isLive, setIsLive] = useState(false);

  const timerRef = useRef<number | null>(null);
  const eventCountRef = useRef(0);
  const hasWarnedRef = useRef(false);
  const mountedRef = useRef(true);

  // Read at fire time so the warning never reports a stale label.
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const armSilenceTimer = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (!mountedRef.current) return;

      setIsLive(false);

      if (!warnOnSilence || hasWarnedRef.current) return;
      hasWarnedRef.current = true;

      // Allowlisted primitives only - no ids, addresses, or payload contents.
      Logger.warn('realtime_channel_silent', {
        channel: channelRef.current,
        silence_seconds: Math.round(silenceMs / 1000),
        events_observed: eventCountRef.current,
        environment: CLIENT_VERSION_INFO.environment,
      });
    }, silenceMs);
  }, [clearTimer, silenceMs, warnOnSilence]);

  const markSubscribed = useCallback(() => {
    // Deliberately does not set `isLive`. A joined socket is not a delivered
    // event, and conflating the two is the defect this hook exists to fix.
    armSilenceTimer();
  }, [armSilenceTimer]);

  const markEvent = useCallback(() => {
    eventCountRef.current += 1;
    if (mountedRef.current) setIsLive(true);
    armSilenceTimer();
  }, [armSilenceTimer]);

  const markDisconnected = useCallback(() => {
    clearTimer();
    if (mountedRef.current) setIsLive(false);
  }, [clearTimer]);

  return { isLive, markSubscribed, markEvent, markDisconnected };
}
